// File: src/pages/neutral/NeutralIntroWizardPage.jsx
import React, { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LuArrowLeft } from 'react-icons/lu';
import {
  IoShieldCheckmarkOutline,
  IoChatbubblesOutline,
  IoPeopleOutline,
} from 'react-icons/io5';
import EventHelpTooltip from '../../components/EventPreview/EventHelpTooltip';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';

/* ───────────────────────── Visual Tokens (Update/Register-aligned) */
const COLOR = {
  bg: '#FFFFFF',
  ink: '#0B1020',
  sub: '#3A4050',
  line: '#E7E9EE',
  chip: '#F3F4F6',
  chipBorder: '#E5E7EB',
  tagBg: '#F7F8FA',
  tagInk: '#333',
  iconBg: '#F3F6FF',
  iconInk: '#2553E6',
};

const GRIDW = 'min(720px, 92vw)';

export default function NeutralIntroWizardPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const event = state?.event || {};

  const title = useMemo(
    () => (event?.name ? event.name : 'This Event'),
    [event?.name]
  );

  const handleEnter = () => {
    if (!eventId) return;

    return navigate(`/neutral/${eventId}/where-to-meet`, {
      state: { event },
    });
  };

  const onHelp = {
    title: 'Before you continue',
    body:
      'Review the group chat expectations first. Next, you’ll see where to meet before entering the chat portal.',
  };

  return (
    <div style={S.app}>
      {/* Header: black back pill, centered logo, tooltip */}
      <header style={S.header}>
        <button
          style={S.backBtn}
          onClick={() => navigate(`/events/${eventId}`, { state: { event } })}
          aria-label="Go back"
        >
          <LuArrowLeft size={16} />
        </button>

        <div style={S.logoWrap} aria-hidden="true">
          <TribeLogo style={S.logoSvg} />
        </div>

        <div style={S.rightWrap}>
          <div style={S.headerRightStack}>
            <EventHelpTooltip title={onHelp.title} body={onHelp.body} />
          </div>
        </div>
      </header>

      {/* Page body */}
      <main style={S.page}>
        <div style={S.headerWrap}>
          <h1 style={S.headerTop}>GET READY TO JOIN</h1>

          <div style={S.headerBottom}>{title}</div>

          <p style={S.subheading}>
            First, review the group chat expectations. Next, we’ll show you
            exactly where to meet before you enter the chat portal.
          </p>
        </div>

        {/* Values & Guidelines */}
        <section style={S.card} aria-labelledby="values-title">
          <div style={S.cardHeader}>
            <span id="values-title" style={S.cardTitle}>
              Chat Guidelines
            </span>
          </div>

          <div style={S.cardBody}>
            <ul style={S.list}>
              <li style={S.listItem}>
                <div style={S.icon}>
                  <IoShieldCheckmarkOutline size={16} />
                </div>

                <div>
                  <div style={S.itemHead}>Respect first</div>
                  <div style={S.itemBody}>
                    Be kind and inclusive. Harassment, hostility, or disrespect
                    are not welcome.
                  </div>
                </div>
              </li>

              <li style={S.listItem}>
                <div style={S.icon}>
                  <IoChatbubblesOutline size={16} />
                </div>

                <div>
                  <div style={S.itemHead}>Use the chat to coordinate</div>
                  <div style={S.itemBody}>
                    Share where you are, ask where the group is, and help others
                    find the meeting spot.
                  </div>
                </div>
              </li>

              <li style={S.listItem}>
                <div style={S.icon}>
                  <IoPeopleOutline size={16} />
                </div>

                <div>
                  <div style={S.itemHead}>Show up for the group</div>
                  <div style={S.itemBody}>
                    Say hello, join the conversation, and participate in the experience.
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </section>
      </main>

      {/* Fixed bottom CTA */}
      <div style={S.buttonBar} role="group" aria-label="Actions">
        <div style={S.buttonRow}>
          <button style={S.primaryBtn} onClick={handleEnter}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Styles (same system as other intro wizards) */
const S = {
  app: {
    fontFamily:
      'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    color: COLOR.ink,
    background: COLOR.bg,
  },

  page: {
    width: GRIDW,
    margin: '14px auto 96px',
    display: 'grid',
    gap: 16,
  },

  /* Header */
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto',
    height: 56,
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto 1fr',
    background: '#fff',
  },

  backBtn: {
    justifySelf: 'start',
    marginLeft: 12,
    background: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: 9999,
    width: 40,
    height: 26,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },

  logoWrap: {
    justifySelf: 'center',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },

  logoSvg: {
    width: 72,
    height: 'auto',
    display: 'block',
  },

  rightWrap: {
    justifySelf: 'end',
    marginRight: 12,
    display: 'flex',
    alignItems: 'center',
    height: 32,
  },

  headerRightStack: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

  /* Headline block */
  headerWrap: {
    textAlign: 'center',
    maxWidth: 640,
    margin: '0 auto',
  },

  headerTop: {
    fontSize: 'clamp(0.98rem, 2.6vw, 1.05rem)',
    fontWeight: 900,
    letterSpacing: '0.06em',
    lineHeight: 1.15,
    margin: '10px 0 0 0',
    color: '#000',
  },

  headerBottom: {
    fontSize: 'clamp(1.8rem, 4.2vw, 2.2rem)',
    lineHeight: 1.25,
    marginTop: 8,
    fontWeight: 900,
    letterSpacing: '-0.01em',
  },

  subheading: {
    fontSize: 13.5,
    color: COLOR.sub,
    lineHeight: 1.55,
    margin: '10px 28px 0 28px',
  },

  /* Values Card */
  card: {
    width: '100%',
    border: `1px solid ${COLOR.line}`,
    borderRadius: 12,
    background: '#FFFFFF',
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
  },

  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },

  cardTitle: {
    fontSize: 14.5,
    fontWeight: 900,
    color: COLOR.ink,
    letterSpacing: '0.01em',
  },

  cardBody: {
    borderTop: `1px solid ${COLOR.line}`,
    padding: '10px 16px 14px',
  },

  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gap: 10,
  },

  listItem: {
    display: 'grid',
    gridTemplateColumns: '22px 1fr',
    gap: 10,
    alignItems: 'start',
  },

  icon: {
    width: 22,
    height: 22,
    display: 'grid',
    placeItems: 'center',
    background: COLOR.iconBg,
    color: COLOR.iconInk,
    borderRadius: 999,
    marginTop: 2,
  },

  itemHead: {
    fontWeight: 800,
    color: COLOR.ink,
    marginBottom: 2,
    fontSize: 14,
  },

  itemBody: {
    color: '#4A5568',
    lineHeight: 1.5,
  },

  /* CTA bar */
  buttonBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '12px 20px calc(12px + env(safe-area-inset-bottom))',
    background: 'transparent',
    pointerEvents: 'none',
  },

  buttonRow: {
    pointerEvents: 'auto',
    margin: '0 auto',
    width: '100%',
    maxWidth: 'none',
    display: 'flex',
  },

  primaryBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    border: '1px solid #0b0b0b',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};